import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'

/**
 * Concat String — joins N string inputs end-to-end and outputs a single
 * string. Useful for things like building an audio URL from a prefix,
 * a dynamic name, and a suffix:
 *
 *   in-0: "/assets/audio/"
 *   in-1: OnPrefabTrigger.animName
 *   in-2: ".wav"
 *   out-value: "/assets/audio/greeting.wav"
 *
 * Missing / unwired inputs contribute empty strings. Each input is
 * coerced via `String(value)` at evaluation time so numeric pins work too.
 */

const PURPLE  = '#c060e8'
const HEADER  = '#2a1038'

export function ConcatStringNode({ data, selected }) {
  const fired    = isFired(data)
  const stop     = e => e.stopPropagation()
  const count    = Math.max(2, data.count ?? 2)
  const preview  = data._preview  // updated by runtime each time the node resolves

  const setCount = (n) => data.onChange?.('count', Math.max(2, n))

  return (
    <div style={{
      background: selected ? '#140820' : '#0c0516',
      border: `2px solid ${fired ? '#ffcc44' : selected ? PURPLE : '#3a2050'}`,
      borderRadius: 6, minWidth: 220, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${PURPLE}33` : 'none',
    }}>
      {/* Header + out pin */}
      <div style={{
        background: HEADER,
        padding: '7px 14px 7px 20px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${PURPLE}33`, borderRadius: '4px 4px 0 0',
        position: 'relative',
      }}>
        <span style={{ color: PURPLE, fontSize: 12, fontWeight: 'bold' }}>⊕</span>
        <span style={{ color: PURPLE, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Concat String
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
          <span style={{ color: '#604070', fontSize: 10 }}>Result</span>
          <Handle type="source" position={Position.Right} id="out-value"
            style={{ background: PURPLE, width: 10, height: 10, borderRadius: '50%' }} />
        </div>
      </div>

      {/* Inputs */}
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center',
          padding: '6px 12px 6px 20px',
          borderBottom: '1px solid #1a1024',
          position: 'relative', gap: 8,
        }}>
          <Handle type="target" position={Position.Left} id={`in-${i}`}
            style={{ background: PURPLE, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${PURPLE}55` }} />
          <span style={{ color: '#806080', fontSize: 10 }}>In {i}</span>
          {count > 2 && (
            <button onMouseDown={stop} onClick={() => setCount(count - 1)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#402050',
                       cursor: 'pointer', fontSize: 13, padding: '0 2px', lineHeight: 1 }}>×</button>
          )}
        </div>
      ))}

      {/* Add pin */}
      <div style={{ padding: '4px 14px', borderBottom: '1px solid #1a1024', display: 'flex', justifyContent: 'flex-end' }}>
        <button onMouseDown={stop} onClick={() => setCount(count + 1)}
          style={{ background: 'none', border: 'none', color: '#604070', fontSize: 10,
                   fontFamily: 'var(--m-font)', cursor: 'pointer' }}>
          Add pin ⊕
        </button>
      </div>

      {/* Preview — last-resolved value, useful for debugging wiring */}
      {preview !== undefined && (
        <div style={{ padding: '5px 14px 7px 20px', color: '#8060a0', fontSize: 9, wordBreak: 'break-all' }}>
          → {String(preview).slice(0, 80)}
        </div>
      )}
    </div>
  )
}
